import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import { Skeleton } from "@/components/base/Loading";
import { useNotifications, type AppNotification } from "@/hooks/useNotifications";
import { notificationMeta } from "@/lib/notification";
import { formatRelativeTime } from "@/lib/format";

export default function NotificationsPage() {
  const { t } = useTranslation();
  const { notifications, loading, error, unreadCount, reload, markRead, markAllRead } = useNotifications();
  const navigate = useNavigate();

  const handleItem = (item: AppNotification) => {
    if (!item.is_read) void markRead(item.id);
    if (item.link) navigate(item.link);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-4xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("notifications.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("notifications.title")}</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  {t("notifications.title")}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t("notifications.subtitle")}
                </p>
              </div>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => void markAllRead()}
                  className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
                >
                  <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                    <i className="ri-check-double-line" />
                  </span>
                  {t("notifications.markAllRead")}
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
          {loading ? (
            <div className="flex flex-col gap-3">
              {[0, 1, 2, 3].map((item) => (
                <div key={item} className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <Skeleton className="h-9 w-9 rounded-md" />
                  <div className="flex-1">
                    <Skeleton className="h-3.5 w-1/3" />
                    <Skeleton className="mt-2.5 h-3 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("notifications.reload")}
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                <i className="ri-notification-off-line text-2xl" />
              </span>
              <p className="text-sm font-medium text-foreground-800">{t("notifications.emptyTitle")}</p>
              <p className="max-w-sm text-xs text-foreground-500">{t("notifications.emptyDesc")}</p>
              <Link
                to="/licenses"
                className="mt-1 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-key-2-line" />
                </span>
                {t("notifications.viewLicenses")}
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {notifications.map((item) => {
                const meta = notificationMeta(item.type);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleItem(item)}
                    className={`flex w-full cursor-pointer items-start gap-3.5 rounded-lg border p-4 text-left transition-colors md:p-5 ${
                      item.is_read
                        ? "border-background-200 bg-background-50 hover:border-primary-300"
                        : "border-primary-200 bg-primary-50/60 hover:border-primary-300"
                    }`}
                  >
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${meta.chip}`}>
                      <i className={`${meta.icon} text-lg`} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-950">{item.title}</span>
                        {!item.is_read && (
                          <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-medium text-accent-800">
                            <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                            {t("notifications.unread")}
                          </span>
                        )}
                      </span>
                      {item.body && (
                        <span className="mt-1.5 block text-xs leading-relaxed text-foreground-600 md:text-sm">
                          {item.body}
                        </span>
                      )}
                      <span className="mt-2 flex items-center gap-1.5 text-[11px] text-foreground-400">
                        <span className="flex h-3.5 w-3.5 items-center justify-center">
                          <i className="ri-time-line" />
                        </span>
                        {formatRelativeTime(item.created_at)}
                      </span>
                    </span>
                    {item.link && (
                      <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center text-foreground-400">
                        <i className="ri-arrow-right-s-line" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <AppFooter />
    </div>
  );
}